import { useEffect, useState } from "react";

export const AUTH_USER_UPDATED_EVENT = "auth-user-updated";
export const AUTH_REFRESH_REQUEST_EVENT = "auth-permissions-refresh-requested";

/*
 * Read routes are listed in the order used for safe redirects.
 * This remains frontend navigation only; backend permission middleware is
 * still the final security boundary.
 */
export const menuAccessMap = [
  { path: "/", moduleKey: "dashboard", action: "read" },
  { path: "/containers", moduleKey: "containers", action: "read" },
  { path: "/tracking", moduleKey: "tracking", action: "read" },
  { path: "/track-shipment", moduleKey: "tracking", action: "read" },
  { path: "/invoices", moduleKey: "invoices", action: "read" },
  { path: "/cost-price-list", moduleKey: "cost-prices", action: "read" },
  { path: "/vendors", moduleKey: "vendors", action: "read" },
  { path: "/payments", moduleKey: "payments", action: "read" },
  { path: "/reports", moduleKey: "reports", action: "read" },
  { path: "/settings", moduleKey: "settings", action: "read" },
  { path: "/help", moduleKey: "help", action: "read" },
  { path: "/profile", moduleKey: "profile", action: "read" },
];

const normalizePathname = (pathname) => {
  const clean = String(pathname || "/").split(/[?#]/)[0] || "/";
  return clean !== "/" ? clean.replace(/\/+$/, "") : "/";
};

const isObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/*
 * Do not grant Super Admin access from a local role string alone.
 * The secured backend returns permissions === "ALL" only for a validated,
 * active system Super Admin account.
 */
export const isAllAccessUser = (user) =>
  Boolean(user && user.permissions === "ALL");

export const getCurrentUser = () => {
  try {
    const saved = localStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

export const setCurrentUser = (user) => {
  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  } else {
    localStorage.removeItem("user");
    localStorage.removeItem("role");
  }

  window.dispatchEvent(
    new CustomEvent(AUTH_USER_UPDATED_EVENT, {
      detail: user || null,
    })
  );
};

export const requestCurrentUserRefresh = () => {
  window.dispatchEvent(new Event(AUTH_REFRESH_REQUEST_EVENT));
};

export const getCurrentPermissions = () => {
  const user = getCurrentUser();

  if (!user) return {};
  if (isAllAccessUser(user)) return "ALL";

  return isObject(user.permissions) ? user.permissions : {};
};

export const hasPermissionForUser = (
  user,
  moduleKey,
  action = "read"
) => {
  if (!user || !moduleKey) return false;
  if (isAllAccessUser(user)) return true;

  const permissions = isObject(user.permissions)
    ? user.permissions
    : {};

  const modulePermission = permissions[moduleKey];

  if (!isObject(modulePermission)) return false;

  const normalizedAction = String(action || "read")
    .trim()
    .toLowerCase();

  const actionKey =
    `can${normalizedAction.charAt(0).toUpperCase()}${normalizedAction.slice(1)}`;

  return Boolean(modulePermission[actionKey]);
};

export const hasPermission = (moduleKey, action = "read") =>
  hasPermissionForUser(getCurrentUser(), moduleKey, action);

/*
 * Resolve the permission required for the current private URL.
 * More-specific routes are checked before their parent routes.
 */
export const getRoutePermission = (pathname) => {
  const path = normalizePathname(pathname);

  if (path === "/") {
    return {
      moduleKey: "dashboard",
      action: "read",
    };
  }

  if (path === "/invoices/create") {
    return {
      moduleKey: "invoices",
      action: "create",
    };
  }

  if (/^\/invoices\/[^/]+\/print$/i.test(path)) {
    return {
      moduleKey: "invoices",
      action: "read",
    };
  }

  const prefixRules = [
    ["/containers", "containers"],
    ["/tracking", "tracking"],
    ["/track-shipment", "tracking"],
    ["/invoices", "invoices"],
    ["/cost-price-list", "cost-prices"],
    ["/vendors", "vendors"],
    ["/payments", "payments"],
    ["/reports", "reports"],
    ["/settings", "settings"],
    ["/help", "help"],
    ["/profile", "profile"],
  ];

  const matched = prefixRules.find(
    ([prefix]) => path === prefix || path.startsWith(`${prefix}/`)
  );

  if (!matched) return null;

  return {
    moduleKey: matched[1],
    action: "read",
  };
};

export const useCurrentUser = () => {
  const [user, setUser] = useState(() => getCurrentUser());

  useEffect(() => {
    const refresh = (event) => {
      if (
        event?.type === AUTH_USER_UPDATED_EVENT &&
        (event.detail === null || isObject(event.detail))
      ) {
        setUser(event.detail);
        return;
      }

      setUser(getCurrentUser());
    };

    window.addEventListener(AUTH_USER_UPDATED_EVENT, refresh);
    window.addEventListener("storage", refresh);

    /*
     * Legacy support: older settings components may still dispatch this.
     * It is intentionally read-only here; ProtectedRoute performs /auth/me.
     */
    window.addEventListener("user-updated", refresh);

    return () => {
      window.removeEventListener(AUTH_USER_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("user-updated", refresh);
    };
  }, []);

  return user;
};

export const getFirstAllowedPath = (user = getCurrentUser()) => {
  if (!user) return "/login";

  if (isAllAccessUser(user)) {
    return "/";
  }

  const firstAllowed = menuAccessMap.find((item) =>
    hasPermissionForUser(user, item.moduleKey, item.action)
  );

  return firstAllowed?.path || "/login";
};

export const getPermissionFingerprint = (user) => {
  if (!user) return "anonymous";

  if (isAllAccessUser(user)) {
    return [
      user.id || "unknown",
      user.roleId || "none",
      user.accessScope || "all",
      "ALL",
    ].join(":");
  }

  const permissions = isObject(user.permissions)
    ? user.permissions
    : {};

  const normalizedPermissions = Object.keys(permissions)
    .sort()
    .map((moduleKey) => {
      const permission = permissions[moduleKey] || {};

      return [
        moduleKey,
        Boolean(permission.canRead),
        Boolean(permission.canCreate),
        Boolean(permission.canUpdate),
        Boolean(permission.canDelete),
      ].join(":");
    })
    .join("|");

  return [
    user.id || "unknown",
    user.roleId || "none",
    user.accessScope || "all",
    normalizedPermissions,
  ].join(":");
};
